/* eslint-disable prefer-rest-params */
/* eslint-disable prefer-spread */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/ban-types */
import axios from 'axios';

import { digestMessage, generateRandomId, getBaseUrl } from '@/components/tracking_third_party/utils/fbcap.utils';
import { isConsentCategoryDisabled } from '@/tscapi/utils/consent';
import { Cookie } from '@/tscapi/utils/cookie';

import { IProduct, IProductEdp } from '../pdp/IProduct';

const randomEventId = generateRandomId(15);

type capiData = {
    event_name?: string;
    event_id?: string;
    event_time?: number;
    user_data?: {
        client_ip_address?: string;
        client_user_agent?: string;
        em?: string;
        ph?: string;
        fn?: string;
        ln?: string;
        ct?: string;
        st?: string;
        zip?: string;
    };
    custom_data?: {
        content_ids?: string[];
        content_type?: string;
        content_name?: string;
        currency?: string;
        value?: number;
        content_category?: string;
    };
    action_source?: string;
    event_source_url?: string;
};
type capiObject = {
    eventConsumer: string;
    eventData: {
        data: [capiData];
        test_event_code?: string;
    };
};
type FacebookPixelObject = {
    test_event_code?: string;
    content_ids?: string[];
    value?: number;
    currency?: string;
    content_type?: string;
    content_name?: string;
    content_category?: string;
};
type EventData = {
    productData: IProduct;
    selectedEdp: IProductEdp;
};
async function buildCapiData(eventData: EventData): Promise<capiData> {
    const cookie = new Cookie();
    const userAgent = window.navigator.userAgent || '';
    const capiData: capiData = {
        event_name: 'ViewContent',
        event_id: randomEventId,
        event_time: Math.floor(Date.now() / 1000),
        action_source: 'website',
        event_source_url: window.location.href,
        user_data: {},
        custom_data: {
            content_ids: [eventData.productData.ItemNo],
            content_type: 'product group',
            content_name: eventData.productData.Name,
            currency: 'CAD',
            value: convertPrice(eventData?.selectedEdp?.AppliedPrice),
        },
    };

    // Add client IP and user agent
    if (cookie.TscClientIP) {
        capiData.user_data.client_ip_address = cookie.TscClientIP;
    }

    if (userAgent) {
        capiData.user_data.client_user_agent = userAgent;
    }

    // If user is signed in, add user data
    if (cookie.UserAccessToken?.Token?.length > 0 && cookie.UserAccessToken.Edp > 0) {
        if (cookie.UserEmail) {
            capiData.user_data.em = await digestMessage(cookie.UserEmail.toLowerCase());
        }
        if (cookie.UserName) {
            capiData.user_data.fn = await digestMessage(cookie.UserName.toLowerCase());
        }
        if (cookie.LastName) {
            capiData.user_data.ln = await digestMessage(cookie.LastName.toLowerCase());
        }
    }

    return capiData;
}
async function buildCategoryViewContent(): Promise<capiData> {
    const cookie = new Cookie();
    const userAgent = window.navigator.userAgent || '';
    const capiData: capiData = {
        event_name: 'ViewContent',
        event_id: randomEventId,
        event_time: Math.floor(Date.now() / 1000),
        action_source: 'website',
        event_source_url: window.location.href,
        user_data: {},
        custom_data: {
            content_category: window?.analyticsData?.page?.category1,
        },
    };

    // Add client IP and user agent
    if (cookie.TscClientIP) {
        capiData.user_data.client_ip_address = cookie.TscClientIP;
    }

    if (userAgent) {
        capiData.user_data.client_user_agent = userAgent;
    }

    // If user is signed in, add user data
    if (cookie.UserAccessToken?.Token?.length > 0 && cookie.UserAccessToken.Edp > 0) {
        if (cookie.UserEmail) {
            capiData.user_data.em = await digestMessage(cookie.UserEmail.toLowerCase());
        }
        if (cookie.UserName) {
            capiData.user_data.fn = await digestMessage(cookie.UserName.toLowerCase());
        }
        if (cookie.LastName) {
            capiData.user_data.ln = await digestMessage(cookie.LastName.toLowerCase());
        }
    }

    return capiData;
}

function buildCapiObject(capiData: capiData): capiObject[] {
    const capiObject: capiObject[] = [{ eventConsumer: 'meta', eventData: { data: [capiData] } }];
    // Add test code if exists
    if (window.tscConfig?.CapiTestCode) {
        capiObject[0].eventData.test_event_code = window.tscConfig.CapiTestCode;
        //todo: remove this line
        // capiObject[0].eventData.test_event_code = 'TEST86224';
    }
    return capiObject;
}

// Export the sendCapiEvent function
export async function sendCapiEvent(capiObject: any) {
    //if consent is not given, prevent fb event
    if (isConsentCategoryDisabled('targeting')) {
        return;
    }
    const URL = '/Tracking/v1/events/';

    const axiosInstance = axios.create({
        baseURL: getBaseUrl(window.location.hostname),
        withCredentials: true,
        headers: {
            Authorization: 'Basic 567e407f-4bc5-463e-9c28-3ac2a2b333ec',
            'Content-Type': 'application/json',
        },
    });

    if (window.tscConfig?.CapiEnabled == 'true') {
        try {
            await axiosInstance.post(URL, capiObject);
        } catch (error) {
            console.log('Error sending event:', error?.response?.data);
        }
    }
}

function sendPixelEvent(fbPixelObject: FacebookPixelObject) {
    //if consent is not given, prevent fb event
    if (isConsentCategoryDisabled('targeting')) {
        return;
    }
    window.fbq('track', 'ViewContent', fbPixelObject, { eventID: randomEventId });
}

function generateBrowserObject(eventDetails: EventData): FacebookPixelObject {
    const fbPixelObject: FacebookPixelObject = {
        content_type: 'product group',
        content_name: eventDetails?.productData?.Name,
        content_ids: [eventDetails?.productData?.ItemNo.toString()],
        value: convertPrice(eventDetails?.selectedEdp?.AppliedPrice),
        currency: 'CAD',
    };
    if (window.tscConfig?.CapiTestCode) {
        fbPixelObject.test_event_code = window.tscConfig.CapiTestCode;
        //todo: remove this line
        // fbPixelObject.test_event_code = 'TEST86224';
    }
    return fbPixelObject;
}
function buildCategoryEventPixel(): FacebookPixelObject {
    const fbPixelObject: FacebookPixelObject = {
        content_category: window?.analyticsData?.page?.category1,
    };
    if (window.tscConfig?.CapiTestCode) {
        fbPixelObject.test_event_code = window.tscConfig.CapiTestCode;
        //todo: remove this line
        // fbPixelObject.test_event_code = 'TEST86224';
    }
    return fbPixelObject;
}

function convertPrice(value) {
    if (window.analyticsData.page.languageCode == 'en_ca') {
        return Number(value.replace(/\$|,/g, ''));
    } else {
        value = value.replace('$', '');
        value = value.replace(',', '.');
        value = value.replace(/\s/g, '');
        return Number(value);
    }
}

document.addEventListener(
    'pdpEvent',
    async function (e: CustomEvent) {
        //server side capi event
        const capiData = await buildCapiData(e?.detail);
        const capiObject = buildCapiObject(capiData);
        sendCapiEvent(capiObject);
        //browser side pixel event
        const fbPixelObject = generateBrowserObject(e?.detail);
        sendPixelEvent(fbPixelObject);
    },
    false,
);

export const trackViewContentEvent = async () => {
    //server side capi event
    const capiData = await buildCategoryViewContent();
    const capiObject = buildCapiObject(capiData);
    sendCapiEvent(capiObject);

    //browser side pixel event
    const fbPixelObject = buildCategoryEventPixel();
    sendPixelEvent(fbPixelObject);
};
